import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" }
const _hoisted_2 = { class: "mb-6" }
const _hoisted_3 = { class: "flex items-center gap-3" }
const _hoisted_4 = { class: "p-2.5 bg-blue-50 rounded-xl" }
const _hoisted_5 = { class: "text-2xl sm:text-3xl font-bold text-dashboard-text" }
const _hoisted_6 = {
  key: 0,
  class: "text-sm text-dashboard-muted mt-0.5"
}
const _hoisted_7 = { class: "bg-white border border-dashboard-border rounded-xl p-4 mb-5 flex items-center gap-3 flex-wrap" }
const _hoisted_8 = {
  key: 0,
  class: "bg-white border border-dashboard-border rounded-xl p-16 text-center"
}
const _hoisted_9 = {
  key: 1,
  class: "bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
}
const _hoisted_10 = { class: "text-sm text-red-600 mt-1" }
const _hoisted_11 = { class: "grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" }
const _hoisted_12 = { class: "grid grid-cols-1 lg:grid-cols-3 gap-5" }
const _hoisted_13 = { class: "bg-white border border-dashboard-border rounded-xl shadow-sm lg:col-span-1" }
const _hoisted_14 = { class: "px-4 py-3 border-b border-dashboard-border flex items-center gap-2" }
const _hoisted_15 = {
  key: 0,
  class: "p-6 text-center text-sm text-dashboard-muted italic"
}
const _hoisted_16 = {
  key: 1,
  class: "divide-y divide-dashboard-border max-h-[640px] overflow-y-auto"
}
const _hoisted_17 = { class: "flex items-center gap-2 min-w-0" }
const _hoisted_18 = { class: "text-sm text-dashboard-text truncate" }
const _hoisted_19 = { class: "text-sm font-semibold text-dashboard-text bg-dashboard-bg px-2 py-0.5 rounded" }
const _hoisted_20 = { class: "bg-white border border-dashboard-border rounded-xl shadow-sm lg:col-span-2 overflow-hidden" }
const _hoisted_21 = { class: "px-4 py-3 border-b border-dashboard-border flex items-center gap-2" }
const _hoisted_22 = {
  key: 0,
  class: "p-12 text-center text-sm text-dashboard-muted italic"
}
const _hoisted_23 = {
  key: 1,
  class: "divide-y divide-dashboard-border max-h-[640px] overflow-y-auto"
}
const _hoisted_24 = ["onClick"]
const _hoisted_25 = { class: "flex-1 min-w-0" }
const _hoisted_26 = { class: "flex items-center gap-2 flex-wrap" }
const _hoisted_27 = { class: "font-medium text-dashboard-text text-sm" }
const _hoisted_28 = { class: "text-xs text-dashboard-muted mt-0.5 flex items-center gap-2 flex-wrap" }
const _hoisted_29 = { class: "font-mono" }

import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  ArrowLeft, User, History, AlertCircle, Activity, ShieldAlert,
  ListChecks, Globe2, BarChart3, ChevronRight,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AuditLogDetailPanel from '@/components/audit/AuditLogDetailPanel.vue'
import StatCard from '@/components/audit/StatCard.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import {
  SEVERITY_META, getActionLabel, getDomainMeta, formatDateTime,
} from '@/utils/auditMeta'
import type { AuditLogEntry } from '@/utils/auditMeta'


export default /*@__PURE__*/_defineComponent({
  __name: 'UserActivityView',
  setup(__props) {

const route = useRoute()
const toast = useToast()

const userId = computed(() => Number(route.params.id))
const recent = ref<AuditLogEntry[]>([])
const stats = ref<Array<{ action: string; count: number }>>([])
const loading = ref(false)
const error = ref('')
const selectedLog = ref<AuditLogEntry | null>(null)

const dateFrom = ref('')
const dateTo = ref('')

const userName = computed(() => {
  const first = recent.value.find(r => r.user?.id === userId.value)?.user
  return first ? `${first.full_name} (@${first.username})` : ''
})

const criticalCount = computed(() => recent.value.filter(e => e.severity === 'critical').length)
const uniqueIps = computed(() => new Set(recent.value.filter(e => e.ip_address).map(e => e.ip_address!)))

async function fetchActivity() {
  loading.value = true
  error.value = ''
  try {
    const params = new URLSearchParams()
    if (dateFrom.value) params.set('from', `${dateFrom.value}T00:00:00`)
    if (dateTo.value) params.set('to', `${dateTo.value}T23:59:59`)
    const qs = params.toString()
    const result = await apiFetch(`/api/admin/audit/user/${userId.value}/${qs ? `?${qs}` : ''}`)
    recent.value = result.recent || []
    stats.value = result.stats_by_action || []
  } catch (e: any) {
    error.value = e?.message || 'Erreur'
    toast.error('Erreur', error.value)
  } finally {
    loading.value = false
  }
}

watch(userId, fetchActivity)
onMounted(fetchActivity)

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(AdminLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_router_link, {
            to: "/audit",
            class: "text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(ArrowLeft), { class: "w-4 h-4" }),
              _cache[5] || (_cache[5] = _createTextVNode(" Retour aux logs d'audit "))
            ]),
            _: 1,
            __: [5]
          }),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(User), { class: "w-6 h-6 text-blue-600" })
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("h1", _hoisted_5, "Activité — Utilisateur #" + _toDisplayString(userId.value), 1),
              (userName.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(userName.value), 1))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-sm text-dashboard-muted" }, "Période :", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateFrom).value = $event)),
            type: "date",
            class: "px-3 py-1.5 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm"
          }, null, 512), [
            [_vModelText, dateFrom.value]
          ]),
          _cache[7] || (_cache[7] = _createElementVNode("span", { class: "text-dashboard-muted" }, "→", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dateTo).value = $event)),
            type: "date",
            class: "px-3 py-1.5 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm"
          }, null, 512), [
            [_vModelText, dateTo.value]
          ]),
          _createElementVNode("button", {
            onClick: fetchActivity,
            class: "px-3 py-1.5 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#22282F]/90"
          }, " Appliquer "),
          (dateFrom.value || dateTo.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                onClick: _cache[2] || (_cache[2] = ($event: any) => {dateFrom.value=''; dateTo.value=''; fetchActivity()}),
                class: "text-xs text-dashboard-muted hover:text-dashboard-text"
              }, " Réinitialiser "))
            : _createCommentVNode("", true)
        ]),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, _cache[8] || (_cache[8] = [
              _createElementVNode("div", { class: "w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mx-auto mb-4" }, null, -1),
              _createElementVNode("p", { class: "text-dashboard-muted" }, "Chargement…", -1)
            ])))
          : (error.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createVNode(_unref(AlertCircle), { class: "w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" }),
                _createElementVNode("div", null, [
                  _cache[9] || (_cache[9] = _createElementVNode("p", { class: "font-medium text-red-800" }, "Erreur", -1)),
                  _createElementVNode("p", _hoisted_10, _toDisplayString(error.value), 1)
                ])
              ]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(StatCard, {
                    label: "Actions récentes",
                    value: String(recent.value.length),
                    icon: _unref(Activity),
                    tone: "slate"
                  }, null, 8, ["value", "icon"]),
                  _createVNode(StatCard, {
                    label: "Types d'actions",
                    value: String(stats.value.length),
                    icon: _unref(ListChecks),
                    tone: "indigo"
                  }, null, 8, ["value", "icon"]),
                  _createVNode(StatCard, {
                    label: "Critiques",
                    value: String(criticalCount.value),
                    icon: _unref(ShieldAlert),
                    tone: "red"
                  }, null, 8, ["value", "icon"]),
                  _createVNode(StatCard, {
                    label: "Adresses IP",
                    value: String(uniqueIps.value.size),
                    icon: _unref(Globe2),
                    tone: "blue"
                  }, null, 8, ["value", "icon"])
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("div", _hoisted_14, [
                      _createVNode(_unref(BarChart3), { class: "w-4 h-4 text-dashboard-muted" }),
                      _cache[10] || (_cache[10] = _createElementVNode("h2", { class: "text-sm font-semibold text-dashboard-text" }, "Top actions", -1))
                    ]),
                    (stats.value.length === 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_15, " Aucune action sur la période. "))
                      : (_openBlock(), _createElementBlock("ul", _hoisted_16, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(stats.value, (s) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: s.action,
                              class: "px-4 py-2.5 flex items-center justify-between gap-2 hover:bg-dashboard-bg"
                            }, [
                              _createElementVNode("div", _hoisted_17, [
                                (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(getDomainMeta)(s.action).icon), { class: "w-3.5 h-3.5 text-dashboard-muted flex-shrink-0" })),
                                _createElementVNode("span", _hoisted_18, _toDisplayString(_unref(getActionLabel)(s.action)), 1)
                              ]),
                              _createElementVNode("span", _hoisted_19, _toDisplayString(s.count), 1)
                            ]))
                          }), 128))
                        ]))
                  ]),
                  _createElementVNode("div", _hoisted_20, [
                    _createElementVNode("div", _hoisted_21, [
                      _createVNode(_unref(History), { class: "w-4 h-4 text-dashboard-muted" }),
                      _cache[11] || (_cache[11] = _createElementVNode("h2", { class: "text-sm font-semibold text-dashboard-text" }, "Chronologie (1000 dernières)", -1))
                    ]),
                    (recent.value.length === 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_22, " Aucune action sur la période. "))
                      : (_openBlock(), _createElementBlock("ul", _hoisted_23, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recent.value, (entry) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: entry.id
                            }, [
                              _createElementVNode("button", {
                                onClick: ($event: any) => (selectedLog.value = entry),
                                class: _normalizeClass([
                    'w-full text-left px-4 py-3 hover:bg-dashboard-bg transition-colors flex items-start gap-3 border-l-4',
                    _unref(SEVERITY_META)[entry.severity].ring,
                  ])
                              }, [
                                (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(getDomainMeta)(entry.action).icon), { class: "w-4 h-4 mt-0.5 text-dashboard-muted flex-shrink-0" })),
                                _createElementVNode("div", _hoisted_25, [
                                  _createElementVNode("div", _hoisted_26, [
                                    _createElementVNode("span", _hoisted_27, _toDisplayString(_unref(getActionLabel)(entry.action)), 1),
                                    (entry.severity !== 'info')
                                      ? (_openBlock(), _createElementBlock("span", {
                                          key: 0,
                                          class: _normalizeClass(['text-[10px] px-1.5 py-0.5 rounded font-medium', _unref(SEVERITY_META)[entry.severity].badge])
                                        }, _toDisplayString(_unref(SEVERITY_META)[entry.severity].label), 3))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createElementVNode("div", _hoisted_28, [
                                    _createElementVNode("span", null, _toDisplayString(_unref(formatDateTime)(entry.created_at)), 1),
                                    (entry.courrier_id)
                                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                          _cache[12] || (_cache[12] = _createElementVNode("span", null, "·", -1)),
                                          _createVNode(_component_router_link, {
                                            to: `/audit/courrier/${entry.courrier_id}`,
                                            onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"])),
                                            class: "text-blue-600 hover:underline"
                                          }, {
                                            default: _withCtx(() => [
                                              _createTextVNode(" Courrier #" + _toDisplayString(entry.courrier_id), 1)
                                            ]),
                                            _: 2
                                          }, 1032, ["to"])
                                        ], 64))
                                      : (entry.target_id)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                            _cache[13] || (_cache[13] = _createElementVNode("span", null, "·", -1)),
                                            _createElementVNode("span", null, _toDisplayString(entry.target_type) + " #" + _toDisplayString(entry.target_id), 1)
                                          ], 64))
                                        : _createCommentVNode("", true),
                                    (entry.ip_address)
                                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                          _cache[14] || (_cache[14] = _createElementVNode("span", null, "·", -1)),
                                          _createElementVNode("span", _hoisted_29, _toDisplayString(entry.ip_address), 1)
                                        ], 64))
                                      : _createCommentVNode("", true)
                                  ])
                                ]),
                                _createVNode(_unref(ChevronRight), { class: "w-4 h-4 text-dashboard-muted flex-shrink-0 mt-1" })
                              ], 10, _hoisted_24)
                            ]))
                          }), 128))
                        ]))
                  ])
                ])
              ], 64))
      ]),
      (selectedLog.value)
        ? (_openBlock(), _createBlock(AuditLogDetailPanel, {
            key: 0,
            log: selectedLog.value,
            onClose: _cache[4] || (_cache[4] = ($event: any) => (selectedLog.value = null))
          }, null, 8, ["log"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})